"use client"

import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Play, Star, ShoppingCart, Clock, X, Megaphone } from "@/lib/icons"
import { formatPrice } from "@/lib/marketplace-store"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

export interface ScriptPromoAd {
  campaign_id: string
  script_id: string
  title: string
  description: string | null
  thumbnail_url: string | null
  /** Purpose-cropped image for the square grid layout — falls back to
   * thumbnail_url when a creator only supplied one image. */
  thumbnail_url_square?: string | null
  preview_video_url: string | null
  price_cents: number
  trial_duration_seconds: number
  avg_rating: number
  purchase_count: number
  tags: string[]
  creator_username: string | null
  creator_avatar: string | null
  /** Set when the creator's ad_image_url already has its own copy/branding
   * baked in — suppresses our own title/description/stats/buttons overlay
   * so the two don't compete for the same space. */
  hide_text_overlay?: boolean
  /** Hides just the "Promo" corner badge — independent of the text overlay. */
  hide_badge?: boolean
  /** Hides just the dark gradient wash behind the text overlay — independent
   * of whether the text itself shows. */
  hide_dim_overlay?: boolean
  is_promo: true
}

interface ScriptPromoCardProps {
  promo: ScriptPromoAd
  onTryNow: (promo: ScriptPromoAd) => void
  onBuyNow: (promo: ScriptPromoAd) => void
  onDismiss?: () => void
  aspectRatio?: "square" | "portrait" | "landscape"
  className?: string
}

export function ScriptPromoCard({
  promo,
  onTryNow,
  onBuyNow,
  onDismiss,
  aspectRatio = "portrait",
  className,
}: ScriptPromoCardProps) {
  const t = useTranslations("scriptPromoCard")
  const [isHovered, setIsHovered] = useState(false)
  const [impressionRecorded, setImpressionRecorded] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  // Record impression when card is visible
  useEffect(() => {
    if (impressionRecorded) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          recordImpression()
          observer.disconnect()
        }
      },
      { threshold: 0.5 },
    )

    if (cardRef.current) {
      observer.observe(cardRef.current)
    }

    return () => observer.disconnect()
  }, [impressionRecorded])

  const recordImpression = async () => {
    setImpressionRecorded(true)
    try {
      await fetch("/api/marketplace/ads/serve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: promo.campaign_id,
          script_id: promo.script_id,
          page_url: window.location.pathname,
          slot_position: "feed",
          device_type: window.innerWidth < 768 ? "mobile" : "desktop",
        }),
      })
    } catch (error) {
      console.error("Failed to record impression:", error)
    }
  }

  const handleClick = async (action: "try" | "buy") => {
    // Record click
    try {
      await fetch("/api/marketplace/ads/serve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          campaign_id: promo.campaign_id,
          script_id: promo.script_id,
          page_url: window.location.pathname,
          slot_position: "feed",
          device_type: window.innerWidth < 768 ? "mobile" : "desktop",
          was_clicked: true,
        }),
      })
    } catch (error) {
      console.error("Failed to record click:", error)
    }

    if (action === "try") {
      onTryNow(promo)
    } else {
      onBuyNow(promo)
    }
  }

  const aspectRatioClass = {
    square: "aspect-square",
    portrait: "aspect-[3/4]",
    landscape: "aspect-video",
  }[aspectRatio]

  return (
    <Card
      ref={cardRef}
      className={cn(
        "group relative overflow-hidden cursor-pointer transition-all",
        "border-primary/30 hover:border-primary/60",
        className,
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Promo Badge */}
      {!promo.hide_badge && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1">
          <Badge className="bg-primary/90 text-primary-foreground backdrop-blur-sm">
            <Megaphone className="h-3 w-3 mr-1" />
            {t("promo")}
          </Badge>
        </div>
      )}

      {/* Dismiss Button */}
      {onDismiss && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 z-10 h-6 w-6 bg-background/50 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss()
          }}
        >
          <X className="h-3 w-3" />
        </Button>
      )}

      {/* Thumbnail — uses the square-cropped image when the surrounding grid
          is in square layout, so a creator's own crop is used instead of
          CSS object-fit auto-cropping a single portrait image. */}
      <div
        className={cn("relative", aspectRatioClass)}
        onClick={promo.hide_text_overlay ? (e) => { e.stopPropagation(); handleClick("try") } : undefined}
      >
        <Image
          src={(aspectRatio === "square" ? promo.thumbnail_url_square : null) || promo.thumbnail_url || "/placeholder.svg?height=400&width=300&query=script preview"}
          alt={promo.title}
          fill
          className="object-cover"
        />

        {/* Dark wash behind the text overlay — independent of whether the
            text itself is shown, so a creator whose image is already dark
            enough can drop just this without losing the title/buttons. */}
        {!promo.hide_dim_overlay && (
          <div
            className={cn(
              "absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent",
              "transition-opacity",
              isHovered ? "opacity-100" : "opacity-70",
            )}
          />
        )}

        {promo.hide_text_overlay ? null : (
        <>
        {/* Content Overlay */}
        <div className="absolute inset-0 flex flex-col justify-end p-3">
          {/* Title and Price */}
          <div className="mb-2">
            <h3 className="text-white font-semibold text-sm line-clamp-1">{promo.title}</h3>
            <p className="text-white/70 text-xs line-clamp-1">
              {promo.description || "Script by " + promo.creator_username}
            </p>
          </div>

          {/* Stats Row */}
          <div className="flex items-center gap-2 mb-3 text-xs text-white/80">
            {promo.avg_rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-yellow-500 text-yellow-500" />
                {promo.avg_rating.toFixed(1)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <ShoppingCart className="h-3 w-3" />
              {t("purchaseCountSold", { purchaseCount: promo.purchase_count })}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {t("trialDurationSecondsSTrial", { trialDurationSeconds: promo.trial_duration_seconds })}
            </span>
          </div>

          {/* Action Buttons */}
          <div
            className={cn(
              "flex gap-2 transition-all",
              isHovered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2",
            )}
          >
            <Button
              size="sm"
              variant="secondary"
              className="flex-1 h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                handleClick("try")
              }}
            >
              <Play className="h-3 w-3 mr-1" />
              {t("tryFree")}
            </Button>
            <Button
              size="sm"
              className="flex-1 h-8 text-xs"
              onClick={(e) => {
                e.stopPropagation()
                handleClick("buy")
              }}
            >
              {formatPrice(promo.price_cents)}
            </Button>
          </div>
        </div>
        </>
        )}
      </div>
    </Card>
  )
}
